import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ChartCard from "../../../common/components/ChartCard";
import OversightSkeleton from "../../../common/components/OversightSkeleton";
import ScholarshipRequestDistrictChart from "./charts/ScholarshipRequestDistrictChart";
import ScholarshipRequestsDepartmentChart from "./charts/ScholarshipRequestsDepartmentChart";
import ScholarshipAcademicYearDistributionChart from "./charts/ScholarshipAcademicYearDistributionChart";
import LayoutControl from "../common/LayoutControl";

const ScholarshipOversight = (props) => {
  const { user, loading } = props;

  const [isLoading1, setIsLoading1] = useState(true);
  const [data, setData] = useState({});
  const [layout, setLayout] = useState("default");
  const [exportLoading, setExportLoading] = useState({ image: false, pdf: false });
  const contentRef = useRef(null);

  useEffect(() => {
    if (user?.selectedEntity) {
      getScholarshipOversights();
    }
  }, [user?.selectedEntity]);

  const getScholarshipOversights = async () => {
    try {
      setIsLoading1(true);
      const { data } = await axios.get(`/api/oversight/scholarship`);

      setData(data);
      setIsLoading1(false);
    } catch (error) {
      setIsLoading1(false);
    }
  };

  const handleExportImage = async () => {
    try {
      setExportLoading(prev => ({ ...prev, image: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const link = document.createElement("a");
      link.download = `Scholarship-Overview-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } catch (error) {
      toast.error("Failed to export image. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, image: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading(prev => ({ ...prev, pdf: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "l" : "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = (pdfHeight - imgHeight * ratio) / 2;

      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio,
        "",
        "FAST"
      );
      pdf.save(`Scholarship-Overview-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, pdf: false }));
    }
  };

  const getLayoutClass = (index) => {
    switch (layout) {
      case "leftWide":
        return index % 2 === 0 ? "col-12 col-md-8 p-0" : "col-12 col-md-4 p-0";
      case "rightWide":
        return index % 2 === 0 ? "col-12 col-md-4 p-0" : "col-12 col-md-8 p-0";
      case "full":
        return "col-12 p-0";
      default:
        return "col-12 col-md-6 p-0";
    }
  };

  if (isLoading1 || loading) {
    return <OversightSkeleton />;
  }

  return (
    <div className="pl-4 pt-2">
      <LayoutControl 
        layout={layout} 
        setLayout={setLayout} 
        onExportImage={handleExportImage}
        onExportPDF={handleExportPDF}
        exportLoading={exportLoading}
      />
      <div className="row w-100" ref={contentRef}>
        {[...Array(4)].map((_, index) => (
          <div key={index} className={getLayoutClass(index)}>
            {index === 0 && (
              <ChartCard
                title="Scholarship Requests by Academic Year"
                subheader="Distribution of scholarship requests by academic year and gender"
              >
                <ScholarshipAcademicYearDistributionChart
                  data={data?.scholarshipRequestAcademicYearDistributions}
                />
              </ChartCard>
            )}
            {index === 1 && (
              <ChartCard
                title="Scholarship Requests Distribution by Department"
                subheader="Distribution of scholarship requests by department and gender"
              >
                <ScholarshipRequestsDepartmentChart
                  data={data?.scholarshipRequestsDepartmentDistributions}
                />
              </ChartCard>
            )}
          </div>
        ))}
        <div className="col-12 p-0">
          <ChartCard
            title="Scholarship Requests Distribution by District"
            subheader="Distribution of scholarship requests by district and gender"
          >
            <ScholarshipRequestDistrictChart
              data={data?.scholarshipRequestsDistrictDistributions}
            />
          </ChartCard>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ user, loading }) => {
  return { user, loading };
};

export default connect(mapStateToProps, {})(ScholarshipOversight);
